import { useEffect, useState } from 'react';
import { Box, TextField } from '@mui/material';

import { S3_FOLDER } from '@/config/constants';
import { useGetSignedUrlMutation } from '@/apis/extended/globalApi';
import { useUpdateProductMutation } from '@/apis/extended/productApi';
import { uploadStorageFile } from '@/utils/storage';
import { Product } from '@/types/ProductType';
import { CommonDialog } from '../Common/CommonDialog';
import { CommonFileInput } from '../Common/CommonFileInput';
import { CommonFormControl } from '../Common/CommonFormControl';
import { CommonNumberInput } from '../Common/CommonNumberInput';

const initialForm = {
  name: '',
  price: '',
  note: '',
  description: '',
};

type EditProductDetailModalProps = {
  isOpen: boolean;
  onClose: () => void;
  product: Product;
};

export const EditProductDetailModal = ({
  isOpen,
  onClose,
  product,
}: EditProductDetailModalProps) => {
  const [updateProduct, { isLoading }] = useUpdateProductMutation();
  const [getSignedUrl] = useGetSignedUrlMutation();

  const [form, setForm] = useState({ ...initialForm });
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imageUrl, setImageUrl] = useState<string>('');
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  useEffect(() => {
    if (!product) return;
    setForm({
      name: product.name,
      price: product.price + '',
      description: product.description,
      note: product.note,
    });
    setImageUrl(product.photo || '');
    setSelectedImage(null);
  }, [product]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleImageChange = (file: File | null) => {
    setSelectedImage(file);
    if (file) {
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setImageUrl(previewUrl);
    }
  };

  const handleUpdateProduct = async () => {
    if (!form.name || !form.price || !form.description) return;

    let uploadedImageUrl = product.photo || '';

    // Upload new image if selected
    if (selectedImage) {
      setIsUploadingImage(true);
      try {
        uploadedImageUrl = await uploadStorageFile(
          selectedImage,
          S3_FOLDER.PRODUCT_IMAGE,
          () =>
            getSignedUrl({
              folderName: S3_FOLDER.PRODUCT_IMAGE,
              fileName: selectedImage.name,
            }).unwrap()
        );
      } catch (error) {
        console.error('Error uploading image:', error);
        setIsUploadingImage(false);
        return;
      }
      setIsUploadingImage(false);
    }

    updateProduct({
      ...form,
      price: parseFloat(form.price),
      photo: uploadedImageUrl || undefined,
      id: product._id,
    })
      .unwrap()
      .then(() => {
        setForm({ ...initialForm });
        setSelectedImage(null);
      });
  };

  return (
    <CommonDialog
      isOpen={isOpen}
      title="Edit Property"
      icon="fa-light fa-file-pen"
      confirmButtonText="Edit Property"
      closeButtonText="Close"
      isLoading={isLoading || isUploadingImage}
      onConfirm={handleUpdateProduct}
      onClose={onClose}
      sx={{
        '& .MuiDialog-paper': {
          width: '600px',
        },
      }}
    >
      <Box>
        <CommonFormControl label="Image:" sx={{ mt: 1 }}>
          <CommonFileInput
            file={selectedImage}
            onFileChange={handleImageChange}
            url={imageUrl}
            accept="image/*"
          />
          {imageUrl && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
              <Box
                component="img"
                src={imageUrl}
                alt="Product preview"
                sx={{
                  maxWidth: '100%',
                  maxHeight: '200px',
                  objectFit: 'contain',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              />
            </Box>
          )}
          <Box sx={{ mt: 1, fontSize: '0.875rem', color: 'text.secondary' }}>
            Accepted formats: JPG, PNG, GIF (Max 10MB)
          </Box>
        </CommonFormControl>

        <CommonFormControl
          label="Address:"
          sx={{ mt: 2 }}
          error={form.name ? '' : 'This field is required!'}
        >
          <TextField
            placeholder="Address"
            name="name"
            fullWidth
            value={form.name}
            onChange={handleInputChange}
          />
        </CommonFormControl>
        <CommonFormControl
          label="Price:"
          sx={{ mt: 1 }}
          error={form.price ? '' : 'This field is required!'}
        >
          <CommonNumberInput
            placeholder="Price"
            name="price"
            fullWidth
            value={form.price}
            onChange={handleInputChange}
          />
        </CommonFormControl>
        <CommonFormControl label="Offer Note:" sx={{ mt: 1 }}>
          <TextField
            placeholder="Offer Note"
            name="note"
            fullWidth
            value={form.note}
            onChange={handleInputChange}
          />
        </CommonFormControl>
        <CommonFormControl
          label="Description:"
          sx={{ mt: 1 }}
          error={form.description ? '' : 'This field is required!'}
        >
          <TextField
            placeholder="Description"
            name="description"
            fullWidth
            multiline
            rows={6}
            value={form.description}
            onChange={handleInputChange}
          />
        </CommonFormControl>
      </Box>
    </CommonDialog>
  );
};
